/**
 * MissionToast — surfaces transient state changes from the mission worker:
 *  • "Aria launched a task in the Office" — appears when a fresh mission
 *    enters `brainstorming` while the founder is in the Execution view.
 *  • "Draft ready for review" — appears when a mission hits `awaiting_approval`.
 *
 * Two CTAs: open the Office (switches mode), or dismiss.
 */
import React, { useEffect, useRef } from 'react';
import { Building2, Sparkles, X } from 'lucide-react';

export default function MissionToast({ mission, kind, onOpenOffice, onDismiss }) {
  // Refs keep the latest handlers but don't reset the auto-dismiss timer when
  // the parent re-renders (which happens every poll cycle).
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;

  // Auto-dismiss the "launched" toast after 7s; "ready" stays until reviewed.
  useEffect(() => {
    if (kind === 'launched') {
      const t = setTimeout(() => dismissRef.current && dismissRef.current(), 7000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [kind, mission?.mission_id]);

  if (!mission) return null;
  const isReady = kind === 'ready';
  const Icon = isReady ? Sparkles : Building2;

  return (
    <div
      data-testid={`mission-toast-${kind}`}
      style={{
        position: 'fixed',
        bottom: 22, right: 22,
        zIndex: 8000,
        minWidth: 320, maxWidth: 380,
        background: '#13131A',
        color: '#F5F5F7',
        border: `1px solid ${isReady ? 'rgba(251,191,36,0.4)' : 'rgba(94,234,212,0.3)'}`,
        borderRadius: 12,
        boxShadow: '0 20px 50px rgba(0,0,0,0.4)',
        padding: 14,
        display: 'flex', gap: 12, alignItems: 'flex-start',
        animation: 'mission-toast-in 0.25s ease-out',
      }}
    >
      <div style={{
        width: 32, height: 32, flexShrink: 0,
        background: isReady ? 'rgba(251,191,36,0.16)' : 'rgba(94,234,212,0.12)',
        color: isReady ? '#FBBF24' : '#5EEAD4',
        border: `1px solid ${isReady ? 'rgba(251,191,36,0.4)' : 'rgba(94,234,212,0.4)'}`,
        borderRadius: 9,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={15} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 9, fontWeight: 800, letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: isReady ? '#FBBF24' : '#5EEAD4',
        }}>
          {isReady ? 'Draft ready for review' : 'Aria launched a task'}
        </div>
        <div style={{
          fontSize: 12.5, fontWeight: 700, color: '#F5F5F7',
          marginTop: 3, lineHeight: 1.4,
        }}>
          {mission.title}
        </div>
        <div style={{
          fontSize: 11, color: 'rgba(255,255,255,0.55)',
          marginTop: 4, lineHeight: 1.5,
        }}>
          {isReady
            ? 'Approve, reject, or request changes — Aria will follow up in chat.'
            : `${(mission.agents || []).join(', ')} are brainstorming in the Office.`}
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          <button
            onClick={onOpenOffice}
            data-testid={`mission-toast-open-${kind}`}
            style={{
              padding: '6px 12px', fontSize: 10.5, fontWeight: 800,
              letterSpacing: '0.04em',
              background: isReady ? '#FBBF24' : '#5EEAD4',
              color: '#0A0A0B',
              border: 'none', borderRadius: 7,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            {isReady ? 'Open review' : 'Peek the Office'}
          </button>
          {kind === 'launched' && (
            <button
              onClick={onDismiss}
              data-testid="mission-toast-dismiss"
              style={{
                padding: '6px 12px', fontSize: 10.5, fontWeight: 700,
                background: 'transparent',
                color: 'rgba(255,255,255,0.55)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Stay here
            </button>
          )}
        </div>
      </div>
      {kind === 'launched' && (
        <button
          onClick={onDismiss}
          style={{
            width: 22, height: 22, padding: 0,
            background: 'transparent', color: 'rgba(255,255,255,0.4)',
            border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <X size={12} />
        </button>
      )}
      <style>{`
        @keyframes mission-toast-in {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
